// src/services/api/dashboard.ts
/**
 * Servizio per la dashboard operativa (home). Un solo endpoint: cosa richiede
 * attenzione oggi, filtrato per ruolo lato backend (GET /api/Dashboard/home).
 */
import { apiClient } from './base';
import { toRequestTypeNum, toRequestStatusNum } from '../../utils/enum-normalizers';
import {
  HomeDashboard,
  HomeRequestItem,
  HomeTaskItem,
  PipelineSection,
  StatusCount,
  MonthlyFlowPoint,
} from '../../types/api';

export class DashboardService {
  private readonly baseUrl = '/api/Dashboard';

  async getHome(): Promise<HomeDashboard> {
    const response = await apiClient.get<any>(`${this.baseUrl}/home`);
    return normalizeHomeDashboard(response.data);
  }
}

export const dashboardService = new DashboardService();

// ================== HELPERS ==================

function normalizeRequestItem(raw: any): HomeRequestItem {
  const r = raw ?? {};
  return {
    id: Number(r.id) || 0,
    progressiveNumberFormatted: r.progressiveNumberFormatted ?? '',
    title: r.title ?? '',
    clientName: r.clientName ?? '',
    type: toRequestTypeNum(r.type) as unknown as HomeRequestItem['type'],
    status: toRequestStatusNum(r.status) as unknown as HomeRequestItem['status'],
    dueDate: r.dueDate ?? undefined,
    createdAt: r.createdAt ?? '',
    daysToDue: r.daysToDue != null ? Number(r.daysToDue) : undefined,
  };
}

function normalizeStatusCount(raw: any): StatusCount {
  const r = raw ?? {};
  return {
    status: toRequestStatusNum(r.status) as unknown as StatusCount['status'],
    count: Number(r.count) || 0,
  };
}

function normalizePipelineSection(raw: any): PipelineSection {
  const r = raw ?? {};
  return {
    key: r.key ?? '',
    type: toRequestTypeNum(r.type) as unknown as PipelineSection['type'],
    openCount: Number(r.openCount) || 0,
    overdueCount: Number(r.overdueCount) || 0,
    statuses: (r.statuses ?? []).map(normalizeStatusCount),
  };
}

function normalizeMonthlyFlowPoint(raw: any): MonthlyFlowPoint {
  const r = raw ?? {};
  return {
    year: Number(r.year) || 0,
    month: Number(r.month) || 0,
    created: Number(r.created) || 0,
    completed: Number(r.completed) || 0,
  };
}

function normalizeTaskItem(raw: any): HomeTaskItem {
  const r = raw ?? {};
  return {
    id: Number(r.id) || 0,
    title: r.title ?? '',
    status: r.status ?? '',
    priority: r.priority ?? '',
    effectiveDueDate: r.effectiveDueDate ?? undefined,
    isInternalDueDate: Boolean(r.isInternalDueDate),
    isOverdue: Boolean(r.isOverdue),
    daysToDue: r.daysToDue != null ? Number(r.daysToDue) : undefined,
    requestId: Number(r.requestId) || 0,
    requestTitle: r.requestTitle ?? '',
    requestProgressiveNumberFormatted: r.requestProgressiveNumberFormatted ?? '',
    requestType: toRequestTypeNum(r.requestType) as unknown as HomeTaskItem['requestType'],
  };
}

function normalizeHomeDashboard(raw: any): HomeDashboard {
  const data = raw ?? {};
  const scope = data.scope ?? {};
  const kpis = data.kpis ?? {};
  const myTasks = data.myTasks ?? {};

  return {
    generatedAtUtc: data.generatedAtUtc ?? '',
    scope: {
      seesStile: Boolean(scope.seesStile),
      seesIndustrializzazione: Boolean(scope.seesIndustrializzazione),
      canSeeAnalytics: Boolean(scope.canSeeAnalytics),
    },
    kpis: {
      openCount: Number(kpis.openCount) || 0,
      overdueCount: Number(kpis.overdueCount) || 0,
      dueSoonCount: Number(kpis.dueSoonCount) || 0,
      createdMonthToDate: Number(kpis.createdMonthToDate) || 0,
      createdPreviousPeriod: Number(kpis.createdPreviousPeriod) || 0,
      completedMonthToDate: Number(kpis.completedMonthToDate) || 0,
      completedPreviousPeriod: Number(kpis.completedPreviousPeriod) || 0,
      periodStartUtc: kpis.periodStartUtc ?? '',
      previousPeriodStartUtc: kpis.previousPeriodStartUtc ?? '',
      previousPeriodEndUtc: kpis.previousPeriodEndUtc ?? '',
    },
    pipeline: (data.pipeline ?? []).map(normalizePipelineSection),
    overdue: (data.overdue ?? []).map(normalizeRequestItem),
    dueSoon: (data.dueSoon ?? []).map(normalizeRequestItem),
    recent: (data.recent ?? []).map(normalizeRequestItem),
    monthlyFlow: (data.monthlyFlow ?? []).map(normalizeMonthlyFlowPoint),
    myTasks: {
      openCount: Number(myTasks.openCount) || 0,
      overdueCount: Number(myTasks.overdueCount) || 0,
      dueSoonCount: Number(myTasks.dueSoonCount) || 0,
      items: (myTasks.items ?? []).map(normalizeTaskItem),
    },
  };
}
